import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Assessment,
  Lock,
  MonetizationOn,
  Refresh as RefreshIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { dashboardApi, bloombergApi } from '../services/api';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getAssetTypeLabel,
  getOperationTypeLabel,
  getStatusColor,
  ASSET_TYPE_COLORS,
} from '../utils/formatters';
import { useApp } from '../App';

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon, trend, color = 'primary' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600} color={`${color}.main`}>
            {value}
          </Typography>
          {subtitle && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
              {trend > 0 ? (
                <TrendingUp fontSize="small" color="success" />
              ) : trend < 0 ? (
                <TrendingDown fontSize="small" color="error" />
              ) : null}
              <Typography
                variant="body2"
                color={trend > 0 ? 'success.main' : trend < 0 ? 'error.main' : 'text.secondary'}
              >
                {subtitle}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}.light`,
            color: `${color}.contrastText`,
            opacity: 0.8,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { showNotification, workspace } = useApp();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [topAssets, setTopAssets] = useState([]);
  const [period, setPeriod] = useState('YTD');
  const [bloombergStatus, setBloombergStatus] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, perfRes, topRes, statusRes] = await Promise.all([
        dashboardApi.getSummary(),
        dashboardApi.getPerformance(period),
        dashboardApi.getTopAssets(5),
        bloombergApi.getStatus(),
      ]);

      setSummary(summaryRes.data);
      setPerformance(perfRes.data);
      setTopAssets(topRes.data);
      setBloombergStatus(statusRes.data);
    } catch (error) {
      showNotification('Ошибка загрузки данных дашборда', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period, workspace]);

  const handleRefresh = async () => {
    await fetchData();
    showNotification('Данные обновлены', 'success');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Prepare chart data
  const assetTypeData = summary?.assetTypeDistribution
    ? Object.entries(summary.assetTypeDistribution).map(([type, percent]) => ({
        name: getAssetTypeLabel(type),
        value: parseFloat(percent),
        color: ASSET_TYPE_COLORS[type] || '#95A5A6',
      }))
    : [];

  const geographyData = summary?.geographyDistribution
    ? Object.entries(summary.geographyDistribution).map(([geo, percent]) => ({
        name: geo,
        value: parseFloat(percent),
        color: geo === 'Russia' ? '#E74C3C' : '#3498DB',
      }))
    : [];

  const holderData = summary?.holderDistribution
    ? Object.entries(summary.holderDistribution).map(([holder, percent]) => ({
        name: holder,
        value: parseFloat(percent),
      }))
    : [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Дашборд Net Wealth
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(e, value) => value && setPeriod(value)}
            size="small"
          >
            <ToggleButton value="1M">1М</ToggleButton>
            <ToggleButton value="3M">3М</ToggleButton>
            <ToggleButton value="YTD">YTD</ToggleButton>
            <ToggleButton value="1Y">1Г</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Обновить данные">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KPICard
            title="Total Net Wealth"
            value={formatCurrency(summary?.totalNAV, 'USD', true)}
            subtitle={`${summary?.ytdPLPercent > 0 ? '+' : ''}${formatPercent(summary?.ytdPLPercent)} YTD`}
            trend={summary?.ytdPLPercent}
            icon={<AccountBalance />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KPICard
            title="YTD P&L"
            value={formatCurrency(summary?.ytdPL, 'USD', true)}
            subtitle={summary?.ytdPL > 0 ? 'Прибыль' : 'Убыток'}
            trend={summary?.ytdPL}
            icon={summary?.ytdPL > 0 ? <TrendingUp /> : <TrendingDown />}
            color={summary?.ytdPL > 0 ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KPICard
            title="Активов"
            value={summary?.totalAssets}
            subtitle={`Ликвидных: ${summary?.liquidAssets}`}
            icon={<Assessment />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KPICard
            title="Cash Position"
            value={formatCurrency(summary?.cashPosition, 'USD', true)}
            icon={<MonetizationOn />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KPICard
            title="Заблокировано"
            value={formatCurrency(summary?.blockedValue, 'USD', true)}
            icon={<Lock />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Asset Type Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 350 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Распределение по типам активов
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={assetTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                    labelLine={false}
                  >
                    {assetTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => `${value}%`} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Geography Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 350 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Распределение по географии
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={geographyData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <YAxis type="category" dataKey="name" width={100} />
                  <RechartsTooltip formatter={(value) => `${value}%`} />
                  <Bar dataKey="value" fill="#3498DB">
                    {geographyData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Holder Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 350 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Распределение по владельцам
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={holderData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(v) => `${v}%`} />
                  <RechartsTooltip formatter={(value) => `${value}%`} />
                  <Bar dataKey="value" fill="#1E3A5F" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Chart */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Доходность портфеля vs Индексы
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performance.slice(-90)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => formatDate(date, 'dd.MM')}
                    interval="preserveStartEnd"
                  />
                  <YAxis tickFormatter={(v) => `${v}%`} />
                  <RechartsTooltip
                    formatter={(value, name) => {
                      const labels = {
                        portfolioReturn: 'Портфель',
                        sp500: 'S&P 500',
                        msci: 'MSCI World',
                      };
                      return [`${value}%`, labels[name] || name];
                    }}
                    labelFormatter={(label) => formatDate(label)}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="portfolioReturn"
                    name="Портфель"
                    stroke="#1E3A5F"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="sp500"
                    name="S&P 500"
                    stroke="#E74C3C"
                    strokeWidth={1}
                    dot={false}
                    strokeDasharray="5 5"
                  />
                  <Line
                    type="monotone"
                    dataKey="msci"
                    name="MSCI World"
                    stroke="#27AE60"
                    strokeWidth={1}
                    dot={false}
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tables Row */}
      <Grid container spacing={3}>
        {/* Top Assets */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Топ-5 активов по стоимости
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Тикер</TableCell>
                      <TableCell>Название</TableCell>
                      <TableCell>Владелец</TableCell>
                      <TableCell align="right">Стоимость</TableCell>
                      <TableCell align="right">Доля</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topAssets.map((asset) => (
                      <TableRow key={asset.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {asset.ticker}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {asset.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={asset.holderCode} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(asset.valueUSD, 'USD')}
                        </TableCell>
                        <TableCell align="right">
                          {formatPercent(asset.portfolioShare)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Operations */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Последние операции
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата</TableCell>
                      <TableCell>Тип</TableCell>
                      <TableCell>Актив</TableCell>
                      <TableCell align="right">Сумма</TableCell>
                      <TableCell>Статус</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary?.recentOperations?.map((op) => (
                      <TableRow key={op.id} hover>
                        <TableCell>{formatDate(op.date)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getOperationTypeLabel(op.type)}
                          </Typography>
                        </TableCell>
                        <TableCell>{op.assetTicker || '-'}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(op.amount, op.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={op.status}
                            size="small"
                            color={getStatusColor(op.status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bloomberg Status */}
      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight={500}>
                Bloomberg Data Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircleIcon
                  sx={{
                    fontSize: 12,
                    color: bloombergStatus?.connected ? 'success.main' : 'warning.main',
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {bloombergStatus?.mode === 'mock' ? 'Mock режим' : 'Live'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Последнее обновление: {formatDate(bloombergStatus?.lastSync, 'dd.MM.yyyy HH:mm')}
              </Typography>
              <Button variant="outlined" size="small" startIcon={<RefreshIcon />}>
                Обновить сейчас
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
